'use client';

import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useDesign } from '@/context/DesignContext';

interface CTASectionProps {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  placeholder?: string;
  trustIndicators?: string[];
  design?: any;
}

export function CTASection({
  title = 'Rejoignez notre communauté',
  subtitle = 'Inscrivez-vous pour recevoir nos offres exclusives et nos conseils santé',
  buttonText = "S'inscrire",
  placeholder = 'Votre email',
  trustIndicators = ['Paiement sécurisé', 'Livraison gratuite', 'Service client 24/7'],
  design: propDesign
}: CTASectionProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  const sectionBg = design?.componentDesignMap?.CTASection?.sectionBackground || 'linear-gradient(90deg, #3B82F6, #8B5CF6)';
  const titleColor = design?.componentDesignMap?.CTASection?.titleColor || '#FFFFFF';
  const subtitleColor = design?.componentDesignMap?.CTASection?.subtitleColor || 'rgba(255,255,255,0.9)';
  const inputBg = design?.componentDesignMap?.CTASection?.inputBackground || '#FFFFFF';
  const inputTextColor = design?.componentDesignMap?.CTASection?.inputTextColor || design?.colors?.text || '#0F172A';
  const inputPlaceholderColor = design?.componentDesignMap?.CTASection?.inputPlaceholderColor || design?.colors?.textLight || '#64748B';
  const buttonStyle = design?.componentDesignMap?.CTASection?.buttonStyle || design?.buttons?.cta;
  const trustColor = design?.componentDesignMap?.CTASection?.trustIndicatorColor || 'rgba(255,255,255,0.8)';
  const trustIconColor = design?.componentDesignMap?.CTASection?.trustIconColor || '#FFFFFF';

  return (
    <section className="py-20 lg:py-32 relative overflow-hidden" style={{ background: sectionBg }}>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyem0tNiA2di00aC0ydjRoMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20" />
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: titleColor }}>{title}</h2>
            <p className="text-xl mb-8" style={{ color: subtitleColor }}>{subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input 
                type="email" 
                placeholder={placeholder}
                className="flex-1 px-6 py-4 rounded-full outline-none focus:ring-4 focus:ring-white/30 placeholder:text-slate-600"
                style={{ background: inputBg, color: inputTextColor }}
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 rounded-full font-semibold whitespace-nowrap transition-colors"
                style={{
                  backgroundColor: buttonStyle?.backgroundColor || '#0F172A',
                  color: buttonStyle?.textColor || '#FFFFFF',
                }}
                onMouseEnter={(e) => {
                  if (buttonStyle?.hoverBackgroundColor) e.currentTarget.style.backgroundColor = buttonStyle.hoverBackgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = buttonStyle?.backgroundColor || '#0F172A';
                }}
              >
                {buttonText}
              </motion.button>
            </div>
            <div className="flex flex-wrap justify-center gap-8 mt-12" style={{ color: trustColor }}>
              {trustIndicators.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Check className="w-5 h-5" style={{ color: trustIconColor }} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}